import { haversine } from './model';
import { getHome, isDesktop } from './pcHome';

const MAX_WP = 9;
const ll = (p) => `${p.lat.toFixed(6)},${p.lon.toFixed(6)}`;

export const routeLink = (p) => `https://www.google.com/maps/dir/?api=1&destination=${ll(p)}&travelmode=driving`;

// last GPS fix, refreshed in the background: route links must open synchronously inside the tap (Android hands them to the Maps app)
let last = null;
export const lastFix = () => last;
export function warmFix() {
  navigator.geolocation?.getCurrentPosition((p) => { last = { lat: p.coords.latitude, lon: p.coords.longitude }; }, () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 120000 });
}

export function currentPosition(fallback) {
  return new Promise((res) => {
    if (!navigator.geolocation) return res({ ...fallback, gps: false });
    navigator.geolocation.getCurrentPosition(
      (pos) => res({ lat: pos.coords.latitude, lon: pos.coords.longitude, gps: true }),
      () => res({ ...fallback, gps: false }),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });
}

// nearest-neighbour order from start; returns stops with leg distance in m
export function orderStops(start, reports) {
  const left = [...reports];
  const out = [];
  let cur = start;
  while (left.length) {
    let bi = 0; let bd = Infinity;
    left.forEach((r, i) => { const d = haversine([cur.lat, cur.lon], [r.lat, r.lon]); if (d < bd) { bd = d; bi = i; } });
    const [r] = left.splice(bi, 1);
    out.push({ ...r, dist: bd });
    cur = r;
  }
  return out;
}

// Google Maps allows 9 waypoints + destination per link
export function routeLegs(start, stops) {
  const legs = [];
  let origin = start;
  for (let i = 0; i < stops.length; i += MAX_WP + 1) {
    const chunk = stops.slice(i, i + MAX_WP + 1);
    const dest = chunk[chunk.length - 1];
    const wps = chunk.slice(0, -1).map(ll).join('|');
    legs.push({ stops: chunk, url: `https://www.google.com/maps/dir/?api=1${origin ? `&origin=${ll(origin)}` : ''}&destination=${ll(dest)}${wps ? `&waypoints=${encodeURIComponent(wps)}` : ''}&travelmode=driving` });
    origin = dest;
  }
  return legs;
}

// shortest round: nearest neighbour + 2-opt (open path from start, or closed loop back to start)
const D = (a, b) => haversine([a.lat, a.lon], [b.lat, b.lon]);
export function optimize(start, stops, loop) {
  if (stops.length < 3) return start ? orderStops(start, stops).map(({ dist: _d, ...p }) => p) : stops;
  const r = start ? orderStops(start, stops).map(({ dist: _d, ...p }) => p) : [...stops];
  const p = start ? [start, ...r] : r, n = p.length, end = loop && start;
  const at = (i) => (i === n ? p[0] : p[i]);
  for (let it = 0, imp = true; imp && it < 50; it += 1) {
    imp = false;
    for (let i = start ? 1 : 0; i < n - 1; i += 1) {
      for (let k = i + 1; k < n; k += 1) {
        const a = p[i - 1], b = p[i], c = p[k], d = k + 1 < n ? p[k + 1] : end ? at(n) : null;
        const before = (a ? D(a, b) : 0) + (d ? D(c, d) : 0), after = (a ? D(a, c) : 0) + (d ? D(b, d) : 0);
        if (after < before - 0.5) { p.splice(i, k - i + 1, ...p.slice(i, k + 1).reverse()); imp = true; }
      }
    }
  }
  return start ? p.slice(1) : p;
}
export const pathLen = (start, ord) => ord.reduce((s, x, i) => s + D(i ? ord[i - 1] : start || x, x), 0);

const KO = 'giss_round_opts';
export const roundOpts = () => { try { return { start: 'here', loop: false, ...JSON.parse(localStorage.getItem(KO)) }; } catch { return { start: 'here', loop: false }; } };
export const setRoundOpts = (o) => { localStorage.setItem(KO, JSON.stringify({ ...roundOpts(), ...o })); window.dispatchEvent(new Event('giss-round-opts')); };

// round from current position (or the first logger of the list), optionally back to the start; keep = list order is fixed
export function planRound(here, stops, keep) {
  const origin = isDesktop() ? getHome() : null;
  const o = roundOpts(), first = o.start === 'first' && stops.length > 1;
  const st = first ? stops[0] : here;
  let ord = keep ? stops : first ? [stops[0], ...optimize(stops[0], stops.slice(1), o.loop)] : optimize(here, stops, o.loop && !!here);
  const loop = o.loop && st;
  if (loop) ord = [...ord, { ...st, id: '__back', name: '↩ Start', title: '↩ Start', back: true }];
  const legs = first ? routeLegs(ord[0], ord.slice(1)) : routeLegs(origin, ord);
  if (first) legs[0].stops = [ord[0], ...legs[0].stops];
  return { ord, legs, km: pathLen(st, ord) / 1000 };
}
